import React from 'react';
import { useSelector } from 'react-redux';
import { ReviewRating, SaveListingButton, ShareButton } from '../../components';
import { FormattedMessage } from 'react-intl';
import { createSlug } from '../../util/urlHelpers';
import { getLocaleFromUrl, LOCALE_TO_LANG } from '../../constants/locales';
import { hasRegionSeoPage, getRegionSeoSlug, getRegionStrapiName } from '../../util/regionSeoMapping';
import { getDisciplineSeoSlug, hasDisciplineSeoPage } from '../../util/disciplineSeoMapping';

import css from './ListingPage.module.css';

// Reusable Breadcrumbs component rendered above the footer to avoid duplicating logic.
export const Breadcrumbs = ({ listing, config, intl, className }) => {
  const { publicData } = listing?.attributes || {};
  const { multiLangAddressParts = {}, discipline = '' } = publicData || {};
  const { interfaceLang } = useSelector(state => ({ interfaceLang: state.ui.interfaceLang }));
  // interfaceLang from Redux is set from the request URL in dataLoader.js (reliable on SSR)
  const locale = interfaceLang || (intl && intl.locale) || getLocaleFromUrl();
  const language = LOCALE_TO_LANG[locale] || (locale ? locale.split('-')[0] : 'en');

  // Pick localized address parts the same way schema does
  const addressParts = multiLangAddressParts[language] || multiLangAddressParts.english || {};
  const { country = '', region = '' } = addressParts;

  const countrySlug = createSlug(country || '');
  
  // Get Strapi SEO slug for region using mapping (localized per language)
  // This maps Mapbox region names to localized Strapi slugs
  // Example: "Kanton Tessin" + "german" → "tessin"
  const regionSeoSlug = getRegionSeoSlug(region, language);
  const regionHasSeoPage = hasRegionSeoPage(region);
  const regionStrapiName = getRegionStrapiName(region, language);

  // Get Strapi SEO slug for discipline using mapping (localized per language)
  // Example: "trailRunning" + "german" → "trailrunning"
  const disciplineSeoSlug = getDisciplineSeoSlug(discipline, language);
  const disciplineHasSeoPage = hasDisciplineSeoPage(discipline);

  const marketplaceRootURL = config?.marketplaceRootURL || '';
  const outdoorDestinationsSlug = intl.formatMessage({ id: 'Schema.outdoorDestinationsSlug' });
  const translatedDiscipline = intl.formatMessage({ id: `General.${discipline}` });

  return (
    <div className={`${css.breadcrumbs} ${className || ''}`}>
      <a className={css.breadcrumbLink} href={`${marketplaceRootURL}/${locale}/`}>
        <FormattedMessage id="Breadcrumb.home" />
      </a>
      <span className={css.breadcrumbSeparator}>›</span>
      <a className={css.breadcrumbLink} href={`${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}`}>
        <FormattedMessage id="Breadcrumb.outdoorDestinations" />
      </a>
      {country ? (
        <>
          <span className={css.breadcrumbSeparator}>›</span>
          <a className={css.breadcrumbLink} href={`${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${countrySlug}`}>
            {country}
          </a>
        </>
      ) : null}
      {region && regionHasSeoPage && regionSeoSlug ? (
        <>
          <span className={css.breadcrumbSeparator}>›</span>
          <a className={css.breadcrumbLink} href={`${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${countrySlug}/${regionSeoSlug}`}>
            {regionStrapiName || region}
          </a>
        </>
      ) : null}
      {discipline && regionHasSeoPage && regionSeoSlug && disciplineHasSeoPage && disciplineSeoSlug ? (
        <>
          <span className={css.breadcrumbSeparator}>›</span>
          <a
            className={css.breadcrumbLink}
            href={`${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${countrySlug}/${regionSeoSlug}/${disciplineSeoSlug}`}
          >
            {translatedDiscipline}
          </a>
        </>
      ) : null}
    </div>
  );
};

const SectionHeading = props => {
  const { richTitle, averageRating, ratingCount, listing } = props;

  return (
    <div className={css.sectionHeading}>
      <h1 className={`${css.title} listing-title`} data-schema="listing-title">{richTitle}</h1>
      <div className={css.headingInfoContainer}>
        {averageRating > 0 ? (
          <ReviewRating rating={averageRating} ratingCount={ratingCount} isAccented />
        ) : (
          <div />
        )}
        <div className={css.headingButtons}>
          <ShareButton>
            <FormattedMessage id="General.share" />
          </ShareButton>
          <SaveListingButton listingId={listing.id.uuid}>
            <FormattedMessage id="General.save" />
          </SaveListingButton>
        </div>
      </div>
    </div>
  );
};

export default SectionHeading;
